import { defaultPersonBetalingsAdrColumns } from "../../../../../config/person";
import { getHeaderActiveColumns, TD } from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import TableBasic, { TableRow } from "../../../../Table/TableBasic";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import { personAltFakturaAdresseFormConfig } from "../../../../../config/form/person/mereOmPerson/personAltFakturaAdresseFormConfig";
import { PersonDto } from "../../../../../interfaces/schema";
import { SortBy } from "../../../../../interfaces/table";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "../../../../accordion/Accordion";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { periodeIsActive } from "../../../../../util/date";
import { orderBy } from "../../../../../util/orderBy";

export default function AlternativFakturaAdresse({
  person,
  refetch,
}: {
  person: PersonDto;
  refetch: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "BasisWrite");
  const [columns, setActiveColumns] = useActiveColumns(
    "person-personbetalingsadresser-activecolumns",
    defaultPersonBetalingsAdrColumns,
  );
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personAlternativFakturaAdresseSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  const addresses = person.personBetalingsadresse ?? [];

  // event handlers
  function downloadHandler() {
    tableToXLSX(columns, addresses ?? [], {
      fileName: `alternativfaktura-for-${person.kundeNr}`,
      additionalData: {
        kundenr: person.kundeNr,
      },
    });
  }

  return (
    <AccordionItem
      className="c-card c-card--accordion"
      value="alternativfaktureringsadresse"
      isEmpty={addresses.length === 0}
    >
      <AccordionHeader>
        <AccordionTrigger>
          <span>Alternativ faktureringsadresse</span>
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent>
        {!addresses.length ? (
          <IngenRegistreringer
            editConfig={
              canWrite
                ? personAltFakturaAdresseFormConfig(person.id)
                : undefined
            }
            invalidateData={refetch}
          />
        ) : (
          <>
            <ButtonDownload onClick={downloadHandler} />
            <TableBasic
              defaultColumns={defaultPersonBetalingsAdrColumns}
              columns={columns}
              setActiveColumns={setActiveColumns}
              headerRow={
                <>
                  <th className="indicator"></th>
                  {getHeaderActiveColumns(columns, {
                    defaultSortBy,
                    setSortBy,
                    sortBy,
                  })}
                </>
              }
            >
              {orderBy(
                addresses,
                sortBy.map((ds) => ds.field),
                sortBy.map((ds) => ds.direction),
              ).map((adr, index) => {
                return (
                  <TableRow
                    key={index}
                    editConfig={
                      canWrite
                        ? personAltFakturaAdresseFormConfig(
                            person.id,
                            adr.id,
                            adr.adresse,
                          )
                        : undefined
                    }
                    invalidateData={refetch}
                  >
                    <td
                      className={`indicator${
                        periodeIsActive(
                          new Date(adr.periode.fraDato),
                          new Date(adr.periode.tilDatoInklusiv),
                        )
                          ? " has-effect"
                          : ""
                      }`}
                    >
                      <ActiveIcon />
                    </td>
                    <>
                      {columns
                        .filter((col) => col.active)
                        .map((col) => {
                          return <TD key={col.field} col={col} rowData={adr} />;
                        })}
                    </>
                  </TableRow>
                );
              })}
            </TableBasic>
            <OpretNyButtonBlock
              editConfig={
                canWrite
                  ? personAltFakturaAdresseFormConfig(person.id)
                  : undefined
              }
              invalidateData={refetch}
            />
          </>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];
